import type { Modal } from 'bootstrap'
import type { OrganizationConfigConfig } from '@/types/organization-config'
import type { UnknownFunction } from '@/utils/util-types.ts'
import axios, { type AxiosResponse } from 'axios'
import { require } from '@logistiek-zonder-papier/lzp-library'
import type { AppInitResponse } from '@/legacy/legacy-main'

function getSaveMissingFromConfig(config: OrganizationConfigConfig | null) {
  const translationOrganizationConfig = config?.parameters?.find(
    parameter => parameter.entity === 'Translation'
  )
  const saveMissing = translationOrganizationConfig?.parameters?.find(
    parameter => parameter.key === 'saveMissing'
  )
  return saveMissing?.value ?? false
}

const translationFrm = `
                <div class="modal fade" id="editTranslationModal" tabindex="-1" aria-labelledby="editTranslationModalLabel" aria-hidden="true">
                    <div class="modal-dialog">
                        <div class="modal-content">
                            <div class="modal-header">
                            <h5 class="modal-title" id="editTranslationModalLabel">Edit Translations</h5>
                            <button type="button" class="btn-close editTranslationModal-close"   data-bs-dismiss="modal" aria-label="Close"></button>
                            </div>
                            <div class="modal-body" id = "translationsContainer">
                                {{#add}}
                                    <strong data-i18n="translation.key" style="font-weight: bold">key</strong>: <br>
                                    <div class="mb-3">
                                        <input type="text" class="form-control translation-input" data-i18n="[placeholder]translation.key"   name="key">
                                    </div>
                                 {{/add}}
                                <ul class="nav nav-tabs" id="languageTabs" role="tablist">
                                    {{#languages}}
                                    <li class="nav-item" role="presentation">
                                        <a class="nav-link {{#isActive}}active{{/isActive}}" language="{{language}}" id="{{language}}-tab" data-bs-toggle="tab" href="#{{language}}" role="tab" aria-controls="{{language}}" aria-selected="{{#isActive}}true{{/isActive}}{{^isActive}}false{{/isActive}}">{{language}}</a>
                                    </li>
                                    {{/languages}}
                                </ul>
                                <div class="tab-content" id="languageTabsContent">
                                    {{#languages}}
                                    <div class="tab-pane fade {{#isActive}}show active{{/isActive}}" id="{{language}}" role="tabpanel"  lang="{{language}}" aria-labelledby="{{language}}-tab">
                                        {{#translationKeys}}
                                        <div class="mb-3">
                                           {{^add}}
                                             <strong  data-i18n="translation.key" style="font-weight: bold">key</strong>: <i>{{key}}</i> <br>
                                             <input type="text" class="form-control translation-input"  language="{{language}}"  name="{{key}}" value="{{value}}">
                                            {{/add}}
                                             {{#add}}
                                                <strong data-i18n="translation.value" style="font-weight: bold">value</strong>
                                                <input type="text" class="form-control translation-input"  data-i18n="[placeholder]translation.value"   language="{{language}}"  name="value" value="{{value}}">
                                            {{/add}}
                                        </div>
                                        {{/translationKeys}}
                                    </div>
                                    {{/languages}}
                                </div>
                            </div>
                            <div class="hidden translation-errors" style="color: red ;margin-left: 10px"  ></div>
                            <div class="modal-footer">
                                <button type="button" class="btn btn-secondary editTranslationModal-close" data-bs-dismiss="modal" data-i18n="common.button.close">Close</button>
                                <button type="button" id="editTranslationModal-saveBtn" class="btn btn-primary" data-i18n="common.button.save">Save Changes</button>
                            </div>
                        </div>
                    </div>
                </div>
            `

export type TabDataLanguageEntry = {
  key: string
  value: string
}

export type TabDataLanguage = {
  language: string | undefined
  isActive: boolean
  translationKeys: TabDataLanguageEntry[]
}

export type TabData = {
  add?: unknown
  languages: TabDataLanguage[]
}

export class I18NEXT {
  public saveUrl: string
  public deleteUrl: string
  public saveMissingUrl: string
  public saveMissing: string
  public savedEvent = new Event('saved')
  public isActive: boolean = false
  public editModal?: Modal
  public dataset?: Record<string, string>
  public tabData?: TabData
  public can_modify_translations: boolean

  constructor(data: AppInitResponse) {
    window.i18EditorOptions = {
      // TODO from orgcon
      saveUrl: `/auth/api/translations/save`,
      deleteUrl: `/auth/api/translations/delete`,
      saveMissingUrl: `/auth/api/translations/save-missing`,
      saveMissing: getSaveMissingFromConfig(data.orgConfig),
      can_modify_translations: data.can_modify_translations,
    }
    this.saveUrl = window.i18EditorOptions.saveUrl
    this.deleteUrl = window.i18EditorOptions.deleteUrl
    this.saveMissingUrl = window.i18EditorOptions.saveMissingUrl
    this.saveMissing = window.i18EditorOptions.saveMissing
    this.can_modify_translations = window.i18EditorOptions.can_modify_translations

    $.extend($.validator.messages, {
      required: () => i18next.t('validation.required'),
    })
  }

  initTranslations(data: AppInitResponse) {
    const languages = (window.supportedLanguages = this.getSupportedLanguages(data.orgConfig))
    i18next.use(i18nextBrowserLanguageDetector).init(
      {
        lng: 'nl', // Default language
        debug: false,
        languages: languages,
        fallbackLng: 'en',
        resources: data.translations,
      },
      (err: unknown) => {
        if (err) {
          return console.error(err)
        }
        jqueryI18next.init(i18next, $, { useOptionsAttr: true })
        $('body').localize()
      }
    )
    jqueryI18next.init(i18next, $, { useOptionsAttr: true })
    $('body').localize()

    const flag = localStorage.getItem('user-language-flag')
    if (flag) {
      $('#selected-flag').attr('class', flag)
    }

    this.setMissingKeyHandler()
  }

  changeLanguage(language: string, flag: string) {
    i18next.changeLanguage(language.toLocaleLowerCase())
    $('body').localize()
    flag = 'align-middle fi ' + flag
    $('#selected-flag').attr('class', flag)
    localStorage.setItem('user-language-flag', flag)
  }

  getSupportedLanguages(config: OrganizationConfigConfig): string[] {
    const languagesFromConfig = config?.parameters
      ?.find(parameter => parameter.entity === 'Translation')
      ?.parameters?.find(parameter => parameter?.key === 'languages')
      ?.options?.map(option => option?.key?.toLowerCase())
    if (languagesFromConfig == null) {
      return ['en', 'nl', 'fr']
    }
    return languagesFromConfig?.filter(language => language != null)
  }

  extractTranslationKeys(
    i18nextAttribute: string,
    value: string
  ): (lang: string) => TabDataLanguageEntry[] {
    if (!i18nextAttribute || !/\[(.*)]/.test(i18nextAttribute)) {
      return (lang: string) => [
        {
          key: i18nextAttribute,
          value:
            value ||
            (i18nextAttribute && i18next.getResource(lang, 'translation', i18nextAttribute)) ||
            i18nextAttribute,
        },
      ]
    }
    const attributes = i18nextAttribute.split(';')
    return (lang: string) =>
      attributes
        .map(attribute => {
          const key = attribute.replaceAll(/\[(.*)]/g, '')
          return {
            key,
            value: value || i18next.getResource(lang, 'translation', key) || key,
          }
        })
        .flat()
  }

  setEditorEvents(add: string) {
    $('.translation-input').on('input', evt => {
      const inpt = $(evt.currentTarget)
      const language =
        inpt.attr('language') || $('#languageTabsContent .tab-pane.active').attr('lang')
      const key = inpt.attr('name')
      const elms = this.tabData?.languages?.find(
        tabLanguage => tabLanguage?.language === language
      )?.translationKeys
      if (!add) {
        const element = elms?.find(element => element?.key === key)
        if (element != null) {
          element.value = String(inpt.val())
        }
      } else {
        if (key === 'key') {
          this.tabData?.languages.forEach(tabLanguage =>
            tabLanguage.translationKeys.forEach(
              translationKey => (translationKey.key = inpt.val() as string)
            )
          )
        }
        if (key && elms) {
          const value = inpt.val()
          if (elms[0] != null) {
            elms[0].key = String(value)
          }
        }
      }
    })

    $('#editTranslationModal-saveBtn').on('click', () => {
      if (confirm(i18next.t('translation.key.save.confirm'))) {
        const callback = () => {
          this.editModal?.hide()
          showToast(i18next.t('translation.key.saved'))
        }
        $('.translation-errors').addClass('hidden')
        if (this.tabData == null) {
          return
        }
        this.saveTranslations(this.saveUrl, this.tabData, callback)
      }
    })
  }

  createModal(template: string, data: TabData) {
    const rendered = Mustache.render(template, data || this.tabData)
    const id = require($(rendered).attr('id'))
    const modelELm = $('#' + id)
    modelELm.remove()

    $(document.body).append($(rendered).localize())

    this.editModal = new bootstrap.Modal(require(document.getElementById(id)))
    this.editModal.show()
  }

  updateLanguageResources(data: TabData | undefined = this.tabData) {
    if (data == null) {
      return
    }
    for (const language of data.languages) {
      for (const key in language.translationKeys) {
        i18next.addResource(language.language, 'translation', key, language.translationKeys[key])
      }
    }
    $('body').localize()
  }

  editTranslation(keys: string, value: string, add: string, data: TabData) {
    if (!data) {
      const keysWithValues = this.extractTranslationKeys(keys, value)

      this.tabData = {
        add,
        languages: window.supportedLanguages.map((lang, index) => ({
          language: lang,
          isActive: index === 0, // Mark first language as active by default
          translationKeys: keysWithValues(lang),
        })),
      }
    } else {
      this.tabData = data
    }

    if (this.tabData == null) {
      return
    }

    this.createModal(translationFrm, this.tabData)
    this.setEditorEvents(add)

    return event
  }

  /*
     when saving the edits by the user
     the reason for sparate @data as input
   */
  async saveTranslations(url: string, data: TabData, callback: UnknownFunction) {
    let res: AxiosResponse<unknown>
    try {
      res = await axios.post(url, data, {
        headers: {
          Authorization: getBearerToken(),
        },
      })
    } catch (err: unknown) {
      if (!axios.isAxiosError(err)) {
        throw err
      }
      const errors = $('.translation-errors')
      errors.removeClass('hidden')
      let msg = err.response?.data || err.message
      msg = i18next.t(msg) || msg
      errors.html(msg)
      return
    }
    if (res.status === 200) {
      this.updateLanguageResources(data)
      if (callback != null) {
        callback()
      }
    } else {
      showErrorToast(JSON.stringify(res.data))
    }
    //POST translation
  }

  setMissingKeyHandler() {
    const transform = (input: {
      language: string[]
      namespace: unknown
      key: string
      value: undefined
    }): TabData => {
      return {
        languages: input.language.map((lang: string, index: number) => ({
          language: lang,
          isActive: index === 0, // Set the first language as active
          translationKeys: [
            {
              key: input.key,
              value: input.value || input.key,
            },
          ],
        })),
      }
    }
    i18next.options.saveMissing = this.saveMissing
    i18next.options.saveMissingTo = 'all'
    i18next.options.missingKeyHandler = (_: unknown, ns: string, key: string) => {
      if (!key) {
        return
      }
      //Prevent the cyclic triggering of the missingKeyHandler for the same key when errors occur in the front-end.
      window.supportedLanguages.forEach(language =>
        i18next.addResource(language, 'translation', key, key)
      )

      const data = transform({
        language: window.supportedLanguages,
        namespace: ns,
        key,
        value: undefined,
      })
      this.saveTranslations(this.saveMissingUrl, data, () => {})
    }
  }

  constructDatatableLanguage() {
    const keyword = 'datatable'
    const resources = $.i18n.getResourceBundle(i18next.language || i18next.fallbackLng)
    const datatableKeys = Object.keys(resources).filter(key => key.startsWith(keyword))
    const datatableStructure: Record<string, string | Record<string, string>> = {}

    datatableKeys.forEach(key => {
      const value = resources[key]
      const path = key.split('.').slice(1) // Remove "datatable" prefix and split

      let currentLevel = datatableStructure
      path.forEach((part, index) => {
        if (index === path.length - 1) {
          // Last part of the path, set the value
          currentLevel[part] = value
        } else {
          // Create nested objects as needed
          if (!currentLevel[part]) {
            currentLevel[part] = {}
          }
          currentLevel = currentLevel[part] as Record<string, string> // ??
        }
      })
    })

    return datatableStructure
  }
}
